import { ArrowUp, ArrowDown, ArrowLeft, ArrowRight } from 'lucide-react'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Slider } from '@/components/ui/slider'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type {
  BrandColor,
  GradientPreset,
  FadeDirection,
  DiagonalCorner,
  OverlayConfig,
} from '@/types'
import { SlotOverlay, BRAND_HEX, GRADIENT_CSS } from './SlotOverlay'

// ── Defaults per overlay type ────────────────────────────────────────────────

const DEFAULTS: Record<OverlayConfig['type'], OverlayConfig> = {
  none:     { type: 'none' },
  gradient: { type: 'gradient', preset: 'full-brand', opacity: 80 },
  solid:    { type: 'solid',    color: 'black',       opacity: 40 },
  fade:     { type: 'fade',     direction: 'to-bottom', color: 'black', fromOpacity: 80, toOpacity: 0 },
  stripe:   { type: 'stripe',   color: 'white', opacity: 30, spacing: 12, strokeWidth: 1, corner: 'top-left' },
}

// ── Sub-components ────────────────────────────────────────────────────────────

const BRAND_COLORS: BrandColor[] = ['orange', 'blue', 'purple', 'yellow', 'black', 'white', 'background']
const BRAND_LABELS: Record<BrandColor, string> = {
  orange: 'Orange', blue: 'Blue', purple: 'Purple',
  yellow: 'Yellow', black: 'Black', white: 'White',
  background: 'Theme background (adapts to light/dark)',
}

function ColorSwatches({
  value,
  onChange,
}: {
  value: BrandColor
  onChange: (c: BrandColor) => void
}) {
  return (
    <div className="flex gap-2">
      {BRAND_COLORS.map((c) => (
        <button
          key={c}
          type="button"
          title={BRAND_LABELS[c]}
          onClick={() => onChange(c)}
          className={cn(
            'h-7 w-7 rounded-full border-2 transition-all overflow-hidden',
            (c === 'white' || c === 'background') && 'border-border',
            value === c ? 'ring-2 ring-primary ring-offset-2' : 'border-transparent',
          )}
          style={c === 'background'
            ? { background: 'linear-gradient(135deg, #f2f2f3 0 50%, #171717 50% 100%)' }
            : { background: BRAND_HEX[c] }}
        />
      ))}
    </div>
  )
}

function SliderRow({
  label,
  value,
  min,
  max,
  step,
  unit,
  onChange,
}: {
  label: string
  value: number
  min: number
  max: number
  step: number
  unit?: string
  onChange: (v: number) => void
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="tabular-nums font-medium">{value}{unit}</span>
      </div>
      <Slider
        min={min} max={max} step={step}
        value={[value]}
        onValueChange={([v]) => onChange(v!)}
      />
    </div>
  )
}

const GRADIENT_PRESETS: { id: GradientPreset; label: string }[] = [
  { id: 'full-brand',    label: 'Brand' },
  { id: 'orange-purple', label: 'Orange → Purple' },
  { id: 'blue-yellow',   label: 'Blue → Yellow' },
  { id: 'orange-blue',   label: 'Orange → Blue' },
  { id: 'purple-yellow', label: 'Purple → Yellow' },
  { id: 'dark-tonal',    label: 'Dark tonal' },
]

const FADE_DIRECTIONS: { id: FadeDirection; icon: React.ReactNode; label: string }[] = [
  { id: 'to-top',    icon: <ArrowUp    className="h-4 w-4" />, label: 'Up' },
  { id: 'to-bottom', icon: <ArrowDown  className="h-4 w-4" />, label: 'Down' },
  { id: 'to-left',   icon: <ArrowLeft  className="h-4 w-4" />, label: 'Left' },
  { id: 'to-right',  icon: <ArrowRight className="h-4 w-4" />, label: 'Right' },
]

const CORNERS: { id: DiagonalCorner; label: string }[] = [
  { id: 'top-left',     label: '↖ Top-left' },
  { id: 'top-right',    label: '↗ Top-right' },
  { id: 'bottom-left',  label: '↙ Bottom-left' },
  { id: 'bottom-right', label: '↘ Bottom-right' },
]

// ── Main component ────────────────────────────────────────────────────────────

interface OverlayPickerProps {
  imageUrl: string
  focusX: number
  focusY: number
  value: OverlayConfig
  onChange: (cfg: OverlayConfig) => void
}

export function OverlayPicker({ imageUrl, focusX, focusY, value, onChange }: OverlayPickerProps) {
  const activeTab = value.type

  function handleTabChange(tab: string) {
    onChange(DEFAULTS[tab as OverlayConfig['type']])
  }

  return (
    <div className="space-y-4">
      {/* Live preview */}
      <div className="relative overflow-hidden rounded-lg border bg-muted/30">
        <img
          src={imageUrl}
          alt="Preview"
          className="block h-auto w-full max-h-[60vh] object-contain"
          style={{ objectPosition: `${focusX * 100}% ${focusY * 100}%` }}
        />
        <SlotOverlay overlay={value} />
      </div>

      {/* Type tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="w-full">
          <TabsTrigger value="none"     className="flex-1">None</TabsTrigger>
          <TabsTrigger value="gradient" className="flex-1">Gradient</TabsTrigger>
          <TabsTrigger value="solid"    className="flex-1">Solid</TabsTrigger>
          <TabsTrigger value="fade"     className="flex-1">Fade</TabsTrigger>
          <TabsTrigger value="stripe"   className="flex-1">Stripe</TabsTrigger>
        </TabsList>

        {/* None */}
        <TabsContent value="none" className="pt-4">
          <p className="text-sm text-muted-foreground">No overlay will be applied to this image.</p>
        </TabsContent>

        {/* Gradient */}
        <TabsContent value="gradient" className="space-y-4 pt-4">
          {value.type === 'gradient' && (
            <>
              <div>
                <p className="mb-2 text-xs text-muted-foreground">Preset</p>
                <div className="grid grid-cols-3 gap-2">
                  {GRADIENT_PRESETS.map(({ id, label }) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => onChange({ ...value, preset: id })}
                      className={cn(
                        'relative h-14 overflow-hidden rounded-md border-2 text-[10px] font-medium transition-all',
                        value.preset === id ? 'border-primary' : 'border-transparent',
                      )}
                    >
                      <div className="absolute inset-0" style={{ background: GRADIENT_CSS[id] }} />
                      <span className="relative z-10 drop-shadow-sm text-white">{label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <SliderRow
                label="Opacity" value={value.opacity} min={0} max={100} step={1} unit="%"
                onChange={(v) => onChange({ ...value, opacity: v })}
              />
            </>
          )}
        </TabsContent>

        {/* Solid */}
        <TabsContent value="solid" className="space-y-4 pt-4">
          {value.type === 'solid' && (
            <>
              <div>
                <p className="mb-2 text-xs text-muted-foreground">Color</p>
                <ColorSwatches value={value.color} onChange={(c) => onChange({ ...value, color: c })} />
              </div>
              <SliderRow
                label="Opacity" value={value.opacity} min={0} max={100} step={1} unit="%"
                onChange={(v) => onChange({ ...value, opacity: v })}
              />
            </>
          )}
        </TabsContent>

        {/* Fade */}
        <TabsContent value="fade" className="space-y-4 pt-4">
          {value.type === 'fade' && (
            <>
              <div>
                <p className="mb-2 text-xs text-muted-foreground">Direction</p>
                <div className="grid grid-cols-4 gap-2">
                  {FADE_DIRECTIONS.map(({ id, icon, label }) => (
                    <Button
                      key={id}
                      type="button"
                      size="sm"
                      variant={value.direction === id ? 'default' : 'outline'}
                      onClick={() => onChange({ ...value, direction: id })}
                      className="flex-col gap-1 h-auto py-2"
                      title={label}
                    >
                      {icon}
                      <span className="text-[10px]">{label}</span>
                    </Button>
                  ))}
                </div>
              </div>
              <div>
                <p className="mb-2 text-xs text-muted-foreground">Color</p>
                <ColorSwatches value={value.color} onChange={(c) => onChange({ ...value, color: c })} />
              </div>
              <SliderRow
                label="Edge opacity" value={value.fromOpacity} min={0} max={100} step={1} unit="%"
                onChange={(v) => onChange({ ...value, fromOpacity: v })}
              />
              <SliderRow
                label="Fade to" value={value.toOpacity} min={0} max={100} step={1} unit="%"
                onChange={(v) => onChange({ ...value, toOpacity: v })}
              />
            </>
          )}
        </TabsContent>

        {/* Stripe */}
        <TabsContent value="stripe" className="space-y-4 pt-4">
          {value.type === 'stripe' && (
            <>
              <div>
                <p className="mb-2 text-xs text-muted-foreground">Color</p>
                <ColorSwatches value={value.color} onChange={(c) => onChange({ ...value, color: c })} />
              </div>
              <SliderRow
                label="Opacity" value={value.opacity} min={0} max={100} step={1} unit="%"
                onChange={(v) => onChange({ ...value, opacity: v })}
              />
              <SliderRow
                label="Spacing" value={value.spacing} min={8} max={40} step={1} unit="px"
                onChange={(v) => onChange({ ...value, spacing: v })}
              />
              <SliderRow
                label="Stroke width" value={value.strokeWidth} min={1} max={4} step={0.5} unit="px"
                onChange={(v) => onChange({ ...value, strokeWidth: v })}
              />
              <div>
                <p className="mb-2 text-xs text-muted-foreground">Corner</p>
                <div className="grid grid-cols-2 gap-2">
                  {CORNERS.map(({ id, label }) => (
                    <Button
                      key={id}
                      type="button"
                      size="sm"
                      variant={value.corner === id ? 'default' : 'outline'}
                      onClick={() => onChange({ ...value, corner: id })}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
